import { Component, OnInit } from '@angular/core';
import { HotkeysService } from 'src/app/_services/hotkeys.service';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-create-tag-dialog',
  templateUrl: './create-tag-dialog.component.html',
  styleUrls: ['./create-tag-dialog.component.scss'],
})
export class CreateTagDialogComponent implements OnInit {
  data: any;
  loading: boolean = false;
  tagCount: number = 0;
  mainHotkey: string = '';
  private nextTagOffset = 0;
  hotkeyForm = this.fb.group({
    hotkey_main: ['', Validators.required],
    hotkey_tag: [''],
    tag_description: [''],
    main_description: ['', Validators.required],
    tags: this.fb.array([]),
  });

  constructor(
    private hotkeysService: HotkeysService,
    private fb: FormBuilder,
    public ref: DynamicDialogRef,
    public messageService: MessageService,
    private config: DynamicDialogConfig
  ) {}

  ngOnInit(): void {
    this.hotkeyForm = this.fb.group({
      hotkey_main: ['', Validators.required],
      hotkey_tag: [''],
      tag_description: [''],
      main_description: ['', Validators.required],
      tags: this.fb.array([]),
    });

    this.data = this.config.data;

    this.tagCount = this.data.descriptions?.length || 0;
    this.mainHotkey = this.data.hotkey_main;

    this.hotkeyForm.patchValue({
      hotkey_main: this.data.hotkey_main,
      main_description: this.data.main_description,
    });

    this.addTag();
  }

  get tagsArray() {
    return this.hotkeyForm.get('tags') as FormArray;
  }

  addTag() {
    const nextTag = this.getNextTag();

    // Column limits (models.py Hotkeys): hotkey_tag VARCHAR(8),
    // tag_description VARCHAR(100) - Postgres rejects an over-length value
    // outright and the generic CRUD route has no error handling around
    // that, so this must be enforced client-side.
    this.tagsArray.push(
      this.fb.group({
        hotkey_tag: [nextTag, [Validators.required, Validators.maxLength(8)]],
        tag_description: ['', [Validators.required, Validators.maxLength(100)]],
      })
    );
  }

  removeTag(index: number) {
    this.tagsArray.removeAt(index);
  }

  createTag() {
    this.loading = true;

    if (this.hotkeyForm.invalid || this.tagsArray.length === 0) {
      this.hotkeyForm.markAllAsTouched();
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill in all fields',
        key: 'hotkeys',
      });
      return;
    }

    const formValue: any = this.hotkeyForm.getRawValue();
    const requests = (formValue.tags || []).map((tag: any) =>
      this.hotkeysService.addHotkey({
        hotkey_main: formValue.hotkey_main,
        main_description: formValue.main_description,
        hotkey_tag: tag.hotkey_tag,
        tag_description: tag.tag_description,
      })
    );

    forkJoin(requests).subscribe(
      (data) => {
        this.hotkeysService.getHotkeys().subscribe((data) => {
          this.hotkeysService.updateHotkeys(data);
        });
      },
      (error) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'An error occurred while creating the hotkey',
          key: 'hotkeys',
        });
      },
      () => {
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Tag created successfully',
          key: 'hotkeys',
        });
        this.ref.close();
      }
    );
  }

  private getNextTag(): string {
    const startIndex =
      this.data?.hotkey_tag === '' ? this.tagCount : this.tagCount + 1;
    const nextTag = `${this.mainHotkey}${startIndex + this.nextTagOffset}`;

    this.nextTagOffset += 1;
    return nextTag;
  }
}
